import React, { useState } from 'react';
import { Bot, Copy, Loader2, MessageSquare, Sparkles } from 'lucide-react';
import { apiGet, apiPost } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

const mailTypes = [
  { value: 'invitation', label: 'Invitation' },
  { value: 'reminder', label: 'Påmindelse' },
  { value: 'rsvp_reminder', label: 'RSVP-påmindelse' },
  { value: 'event_update', label: 'Eventopdatering' },
  { value: 'thank_you', label: 'Takkemail' },
  { value: 'custom', label: 'Tilpasset' },
];

const tones = [
  { value: 'friendly', label: 'Venlig' },
  { value: 'professional', label: 'Professionel' },
  { value: 'funny', label: 'Sjov' },
  { value: 'corporate', label: 'Virksomhedsagtig' },
];

export default function AdminAI() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [question, setQuestion] = useState('');
  const [assistantTemplate, setAssistantTemplate] = useState('summaries');
  const [answer, setAnswer] = useState('');
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [form, setForm] = useState({
    type: 'invitation',
    tone: 'friendly',
    recipient: '',
    context: '',
    extra: '',
  });
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);

  const updateForm = (key, value) => setForm(current => ({ ...current, [key]: value }));

  React.useEffect(() => {
    apiGet('/api/admin/ai/health')
      .then(res => setTemplates(res.data.templates || []))
      .catch(() => setTemplates([]));
  }, []);

  const askAssistant = async (e) => {
    e.preventDefault();
    if (!question.trim()) {
      toast({ title: 'Spørgsmål mangler', description: 'Skriv hvad assistenten skal hjælpe med.', variant: 'destructive' });
      return;
    }
    setAssistantLoading(true);
    setAnswer('');
    try {
      const res = await apiPost('/api/admin/ai/assistant', { question, template: assistantTemplate });
      setAnswer(res.data.answer || '');
      toast({ title: 'Svar genereret', description: 'AI-assistenten har lavet et forslag.' });
    } catch (err) {
      toast({
        title: 'AI-assistenten svarede ikke',
        description: err.response?.data?.detail || 'Tjek AI-konfigurationen og prøv igen.',
        variant: 'destructive',
      });
    } finally {
      setAssistantLoading(false);
    }
  };

  const generateDraft = async (e) => {
    e.preventDefault();
    if (!form.recipient.trim()) {
      toast({ title: 'Modtager mangler', description: 'Beskriv hvem mailen er til.', variant: 'destructive' });
      return;
    }
    if (!form.context.trim() && !form.extra.trim()) {
      toast({ title: 'Instruktioner mangler', description: 'Tilføj kontekst eller instruktioner før generering.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setDraft(null);
    try {
      const res = await apiPost('/api/admin/ai/email', form);
      setDraft(res.data);
      toast({ title: 'Kladde genereret', description: 'AI-mailkladden er klar til at kopiere.' });
    } catch (err) {
      toast({
        title: 'Generering mislykkedes',
        description: err.response?.data?.detail || 'Tjek AI-konfigurationen og prøv igen.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyDraft = async () => {
    if (!draft) return;
    const text = `Emne: ${draft.subject}\n\n${draft.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Kopieret', description: 'Emne og tekst er kopieret.' });
    } catch {
      toast({ title: 'Kopiering mislykkedes', description: 'Markér kladdeteksten manuelt.', variant: 'destructive' });
    }
  };

  return (
    <div className="animate-fade-in" data-testid="admin-ai-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <Bot className="w-6 h-6 text-purple-400" /> AI-assistent
          </h1>
          <p className="text-sm text-slate-400 mt-1">Generér adminkladder uden at sende mail.</p>
        </div>
      </div>

      <section className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 mb-5">
        <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2 mb-5">
          <MessageSquare className="w-5 h-5 text-purple-400" /> Eventcopilot
        </h2>
        <form onSubmit={askAssistant} className="grid grid-cols-1 lg:grid-cols-[220px_1fr_auto] gap-3">
          <select value={assistantTemplate} onChange={e => setAssistantTemplate(e.target.value)}
            className="w-full bg-slate-900/60 border border-slate-700 focus:border-purple-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm">
            {(templates.length ? templates : [{ key: 'summaries', title: 'Opsummeringer' }]).map(item => (
              <option key={item.key} value={item.key}>{item.title}</option>
            ))}
          </select>
          <input value={question} onChange={e => setQuestion(e.target.value)}
            placeholder="Spørg om sikre eventdata, taler, jokes, musik eller opsummeringer"
            className="w-full bg-slate-900/60 border border-slate-700 focus:border-purple-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm" />
          <Button type="submit" disabled={assistantLoading} className="bg-purple-600 hover:bg-purple-500 text-white">
            {assistantLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Spørg
          </Button>
        </form>
        {answer && (
          <pre className="mt-4 whitespace-pre-wrap rounded-lg border border-slate-700 bg-slate-900/50 p-4 text-sm text-slate-200 font-sans leading-relaxed">{answer}</pre>
        )}
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,420px)_1fr] gap-5">
        <section className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2 mb-5">
            <Sparkles className="w-5 h-5 text-blue-400" /> AI-mailskriver
          </h2>

          <form onSubmit={generateDraft} className="space-y-4" data-testid="ai-mail-writer-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Mailtype">
                <select value={form.type} onChange={e => updateForm('type', e.target.value)}
                  className="w-full bg-slate-900/60 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm"
                  data-testid="ai-mail-type">
                  {mailTypes.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </Field>
              <Field label="Tone">
                <select value={form.tone} onChange={e => updateForm('tone', e.target.value)}
                  className="w-full bg-slate-900/60 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm"
                  data-testid="ai-mail-tone">
                  {tones.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Modtagerbeskrivelse">
              <input type="text" value={form.recipient} onChange={e => updateForm('recipient', e.target.value)}
                placeholder="Fx gæster der ikke har svaret på RSVP"
                className="w-full bg-slate-900/60 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm"
                data-testid="ai-mail-recipient" />
            </Field>

            <Field label="Eventkontekst">
              <textarea rows={4} value={form.context} onChange={e => updateForm('context', e.target.value)}
                placeholder="Datoer, deadlines, invitationskoder, dresscode, praktiske detaljer..."
                className="w-full bg-slate-900/60 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm resize-none"
                data-testid="ai-mail-context" />
            </Field>

            <Field label="Ekstra instruktioner">
              <textarea rows={4} value={form.extra} onChange={e => updateForm('extra', e.target.value)}
                placeholder="Noget kladden skal inkludere, undgå eller formulere på en bestemt måde."
                className="w-full bg-slate-900/60 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm resize-none"
                data-testid="ai-mail-extra" />
            </Field>

            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white" data-testid="ai-mail-generate">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? 'Genererer...' : 'Generér'}
            </Button>
          </form>
        </section>

        <section className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 min-h-[360px]">
          <div className="flex items-center justify-between gap-3 mb-5">
            <h2 className="text-lg font-semibold text-slate-100">Genereret kladde</h2>
            <Button type="button" variant="outline" onClick={copyDraft} disabled={!draft}
              className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-700"
              data-testid="ai-mail-copy">
              <Copy className="w-4 h-4" /> Kopiér
            </Button>
          </div>

          {loading ? (
            <div className="h-64 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Genererer kladde...
            </div>
          ) : draft ? (
            <div className="space-y-4" data-testid="ai-mail-result">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Emne</p>
                <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 text-slate-100">{draft.subject}</div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Tekst</p>
                <pre className="whitespace-pre-wrap rounded-lg border border-slate-700 bg-slate-900/50 p-4 text-sm text-slate-200 font-sans leading-relaxed">{draft.body}</pre>
              </div>
              <p className="text-xs text-slate-500">
                Model: {draft.model || 'ukendt'}{draft.tokens_used ? ` · Tokens: ${draft.tokens_used}` : ''}
              </p>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-center text-slate-500">
              <p className="max-w-sm text-sm">Udfyld formularen for at generere emne og mailtekst.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm text-slate-400 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
